import { DateTime, Settings } from 'luxon';
import { z } from 'zod/v4-mini';

import {
  ApiDataSourceSpec,
  apiDataSource,
} from '@/io/datasource/ApiDatasource';
import { BGMData, bgmDataSchema } from '@/models/BGMLogbook';
import {
  BGMGlycemiaParameters,
  CGMGlycemiaParameters,
  EditBGMGlycemiaParameters,
  EditCGMGlycemiaParameters,
  bgmGlycemiaParametersSchema,
  cgmGlycemiaParametersSchema,
} from '@/models/DiabetesDataModel.ts';
import {
  GlycemiaIndicatorsResponse,
  glycemiaIndicatorsSchema,
} from '@/models/GlycemiaStatsModel';
import {
  InsulinContinuousIndicators,
  InsulinIndicatorsResponse,
  insulinContinuousIndicatorsSchema,
  insulinIndicatorsSchema,
} from '@/models/InsulinStatsModel';
import {
  MedicalDevice,
  medicalDeviceSchema,
} from '@/models/MedicalDeviceModel';
import { activitySchema } from '@/models/diabetes/ActivityModel.ts';
import { foodSchema } from '@/models/diabetes/FoodModel.ts';
import { glycemiaSchema } from '@/models/diabetes/GlycemiaModel';
import { reportSchema } from '@/models/diabetes/ReportModel.ts';
import { DateUtils } from '@/utils/dateUtils.ts';
import { cz } from '@/utils/zodUtils.ts';

export class DiabetesRepository {
  constructor(private readonly datasource: ApiDataSourceSpec = apiDataSource) {}

  getMedicalDevices = (patientId: string): Promise<MedicalDevice[]> => {
    return this.datasource.get(
      `/practitioner/devices/${patientId}/`,
      cz.forcedArray(medicalDeviceSchema),
    );
  };

  getBGMLogbook = (
    patientId: string,
    from: DateTime,
    to: DateTime,
  ): Promise<BGMData[]> =>
    this.datasource.get(
      `/bgm-logbook/${patientId}`,
      cz.forcedArray(bgmDataSchema),
      {
        params: {
          from: from.startOf('day').toISO(),
          to: to.endOf('day').toISO(),
        },
      },
    );

  getGlycemia = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(
      `/practitioner/glycemia/${patientId}/`,
      cz.forcedArray(glycemiaSchema),
      {
        params: {
          start: from.toISO(),
          end: to.toISO(),
        },
      },
    );
  };

  getFood = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(
      `/practitioner/food/${patientId}/`,
      cz.forcedArray(foodSchema),
      {
        params: {
          start: from.toISO(),
          end: to.toISO(),
        },
      },
    );
  };

  getActivity = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(
      `/practitioner/activity/${patientId}/`,
      cz.forcedArray(activitySchema),
      {
        params: {
          start: from.toISO(),
          end: to.toISO(),
        },
      },
    );
  };

  getReports = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(
      `/practitioner/reports/${patientId}/`,
      cz.forcedArray(reportSchema),
      {
        params: {
          start: from.toISO(),
          end: to.toISO(),
        },
      },
    );
  };

  getCGMParameters = (patientId: string): Promise<CGMGlycemiaParameters> => {
    return this.datasource.get(
      `/practitioner/parameters/cgm/${patientId}/`,
      cgmGlycemiaParametersSchema,
    );
  };

  editCGMParameters = ({
    id,
    ...parameters
  }: EditCGMGlycemiaParameters): Promise<CGMGlycemiaParameters> => {
    return this.datasource.patch(
      `/practitioner/parameters/cgm/${id}/`,
      cgmGlycemiaParametersSchema,
      parameters,
    );
  };

  getBGMParameters = (patientId: string): Promise<BGMGlycemiaParameters> => {
    return this.datasource.get(
      `/practitioner/parameters/bgm/${patientId}/`,
      bgmGlycemiaParametersSchema,
    );
  };

  editBGMParameters = ({
    id,
    ...parameters
  }: EditBGMGlycemiaParameters): Promise<BGMGlycemiaParameters> => {
    return this.datasource.patch(
      `/practitioner/parameters/bgm/${id}/`,
      bgmGlycemiaParametersSchema,
      parameters,
    );
  };

  getGlycemiaIndicators = (
    patientId: string,
    from: DateTime<true>,
    to: DateTime<true>,
    glycemiaMeasurementMethod: string,
  ): Promise<GlycemiaIndicatorsResponse> => {
    return this.datasource.get(
      `/practitioner/indicators/glycemia/${patientId}/`,
      glycemiaIndicatorsSchema,
      {
        params: {
          start_date: from.startOf('day').toISO(),
          end_date: to.plus({ day: 1 }).startOf('day').toISO(),
          glycemia_measurement_method: glycemiaMeasurementMethod,
          timezone_utc_offset: DateUtils.tzOffset(from) / 60,
        },
      },
    );
  };

  getInsulinDiscreteIndicators = (
    patientId: string,
    from: DateTime<true>,
    to: DateTime<true>,
  ): Promise<InsulinIndicatorsResponse> => {
    return this.datasource.get(
      `/practitioner/indicators/insulin/${patientId}/`,
      insulinIndicatorsSchema,
      {
        params: {
          start_date: from.startOf('day').toISO(),
          end_date: to.plus({ day: 1 }).startOf('day').toISO(),
          insulin_injection_method: 'pen',
          timezone_utc_offset: DateUtils.tzOffset(from) / 60,
        },
      },
    );
  };

  getInsulinContinuousIndicators = (
    patientId: string,
    from: DateTime<true>,
    to: DateTime<true>,
  ): Promise<InsulinContinuousIndicators> => {
    return this.datasource.get(
      `/practitioner/indicators/insulin/${patientId}/`,
      insulinContinuousIndicatorsSchema,
      {
        params: {
          start_date: from.startOf('day').toISO(),
          end_date: to.plus({ day: 1 }).startOf('day').toISO(),
          insulin_injection_method: 'pump',
          timezone_utc_offset: DateUtils.tzOffset(from) / 60,
        },
      },
    );
  };

  uploadFile = (patientId: string, file: File, timezone?: string) => {
    const data = new FormData();
    data.append('timezone', timezone ?? Settings.defaultZone.name);
    data.append('name', file.name);
    data.append('file', file);
    return this.datasource.post(
      `/diabetes/file/${patientId}/`,
      cz.ignore(),
      data,
      {
        headers: {
          'Content-Disposition': `attachment; filename=${file.name}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  };

  getDexcomAuthorizeURL = () => {
    return this.datasource.get(
      '/patient/dexcom/authorize-url/',
      cz.unsafe<{ url?: string; status: string }>(),
    );
  };

  dexcomAuthCallback = (code: string) => {
    return this.datasource.post(`/patient/dexcom/callback/`, z.unknown(), {
      code,
    });
  };
}
